<template>
    <v-row>
        <v-col cols="12">
            <v-row dense justify="space-between" align="center">
                <v-col cols="auto" order="2" order-lg="first">
                    <v-btn icon @click="$refs.calendar.prev()">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                </v-col>
                <v-col cols="12" lg="auto">
                    <v-btn-toggle
                        v-model="calendar_type"
                        mandatory
                        color="primary"
                        rounded
                        class="elevation-0"
                    >
                        <v-btn value="month" small class="d-flex align-center" :class="{ 'justify-center': $vuetify.breakpoint.smAndDown, 'justify-start': $vuetify.breakpoint.mdAndUp }">
                            <v-icon small :left="$vuetify.breakpoint.mdAndUp" :color="calendar_type === 'month' ? 'primary' : 'currentColor'">mdi-calendar-month</v-icon>
                            <span class="hidden-sm-and-down">Mês</span>
                        </v-btn>
                        <v-btn value="week" small class="d-flex align-center" :class="{ 'justify-center': $vuetify.breakpoint.smAndDown, 'justify-start': $vuetify.breakpoint.mdAndUp }">
                            <v-icon small :left="$vuetify.breakpoint.mdAndUp" :color="calendar_type === 'week' ? 'primary' : 'currentColor'">mdi-calendar-week</v-icon>
                            <span class="hidden-sm-and-down">Semana</span>
                        </v-btn>
                        <v-btn value="day" small class="d-flex align-center" :class="{ 'justify-center': $vuetify.breakpoint.smAndDown, 'justify-start': $vuetify.breakpoint.mdAndUp }">
                            <v-icon small :left="$vuetify.breakpoint.mdAndUp" :color="calendar_type === 'day' ? 'primary' : 'currentColor'">mdi-calendar-today</v-icon>
                            <span class="hidden-sm-and-down">Dia</span>
                        </v-btn>
                    </v-btn-toggle>
                </v-col>
                <v-col v-if="$refs.calendar" cols="auto" order="3">
                    <h3>{{ $refs.calendar.title.toLocaleString('default', { month: 'long' }) }}</h3>
                </v-col>
                <v-col cols="auto" order="last">
                    <v-btn icon @click="$refs.calendar.next()">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-sheet height="760" class="rounded-sm overflow-hidden">
                        <v-calendar
                            ref="calendar"
                            v-model="calendar_value"
                            :type="calendar_type"
                            :events="localEvents"
                            :first-time="firstTime"
                            :interval-count="intervalCount"
                            color="primary"
                            :event-ripple="false"
                            @change="getCalendarEvents"
                            @click:event="showEvent"
                            @click:more="viewDay"
                            @click:date="viewDay"
                            @mousedown:event="startDrag"
                            @mousemove:time="mouseMove"
                            @mouseup:time="endDrag"
                            @mouseleave.native="cancelDrag"
                        >
                            <template v-slot:day-body="{ date }">
                                <div
                                    :class="{ first: date === today, 'v-current-time': date === today }"
                                    :style="{ top: nowY }"
                                />
                            </template>
                            <template v-slot:event="{ event }">
                                <v-card 
                                    class="px-1 overflow-hidden"
                                    :class="{ 
                                        'selected-event': selectedSession && event.session && event.session.id === selectedSession.id,
                                        'dragging-event': dragEvent && event.session && dragEvent.session && event.session.id === dragEvent.session.id
                                    }"
                                    width="100%"
                                    style="white-space: nowrap;"
                                    height="100%"
                                    :color="getEventColor(event)"
                                    elevation="0"
                                    dark
                                >
                                    <v-row no-gutters align="center" class="fill-height">
                                        <v-col class="d-flex align-center overflow-hidden">
                                            <!-- Time Discrepancy Warning -->
                                            <v-tooltip bottom v-if="checkTimeDiscrepancy(event)">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon 
                                                        x-small 
                                                        left 
                                                        color="yellow accent-2"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                    >
                                                        mdi-alert
                                                    </v-icon>
                                                </template>
                                                <span>Horário diferente do Google Calendar</span>
                                            </v-tooltip>

                                            <span class="text-caption font-weight-bold white--text text-truncate">{{ event.name }}</span>
                                        </v-col>
                                        <v-avatar v-if="event.session && event.session.google_calendar_event" size="16" class="position-absolute bottom-0 left-0 ma-1 bg-white">
                                            <v-icon 
                                                x-small 
                                                color="primary"
                                            >
                                                mdi-google
                                            </v-icon>
                                        </v-avatar>
                                    </v-row>
                                </v-card>
                            </template>
                        </v-calendar>
                    </v-sheet>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-card elevation="0" color="surface" class="rounded-xl mt-4 pa-2">
                        <v-row align="center" justify="center">
                            <v-col cols="auto" class="text-caption font-weight-bold secondary--text">
                                Legenda:
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-attended" class="white--text font-weight-bold">Compareceu</v-chip>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-missed" class="white--text font-weight-bold">Faltou</v-chip>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-rescheduled" class="white--text font-weight-bold">Remarcou</v-chip>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-scheduled" class="white--text font-weight-bold">Agendadas</v-chip>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-extra" class="white--text font-weight-bold">Extra</v-chip>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-payment-pending" class="white--text font-weight-bold">Pagamento pendente</v-chip>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-birthday" class="white--text font-weight-bold">Aniversário</v-chip>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip small color="event-predicted" class="white--text font-weight-bold">Previsto</v-chip>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import dayjs from 'dayjs';



export default {
    data() {
        return {
            calendar_type: this.$vuetify.breakpoint.mobile ? 'day' : 'week',
            calendar_types: [
                { value: 'month', text: 'Visualizar mês' },
                { value: 'week', text: 'Visualizar semana' },
                { value: 'day', text: 'Visualizar dia' }
            ],
            calendar_value: '',
            ready: false,
            today: dayjs().format('YYYY-MM-DD'),
            selectedEvent: null,
            overlayStyle: {
                top: 0,
                left: 0
            },
            customeEventSchedule: {},
            dragEvent: null,
            dragTime: null,
            createEvent: null,
            createStart: null,
            extendOriginal: null,
            localEvents: [],
            dragTimeout: null,
            isDraggingReady: false,
            hasDragged: false
        }
    },
    watch: {
        events: {
            handler(val) {
                this.localEvents = JSON.parse(JSON.stringify(val || []));
            },
            deep: true,
            immediate: true
        }
    },
    props: {
        selectedSession: {
            type: Object,
            default: null
        }
    },
    computed: {
        ...mapState("schedule", {
            events: state => state.calendar_events
        }),
        ...mapState("auth", {
            user: state => state.user
        }),
        firstTime() {
            if (!this.user?.availabilities || this.user.availabilities.length === 0) {
                return '10:00';
            }
            
            // Filter out availabilities with null start_time (not_attending days)
            const startTimes = this.user.availabilities
                .filter(a => a.start_time !== null)
                .map(a => a.start_time);
            
            if (startTimes.length === 0) {
                return '10:00';
            }
            
            const minTime = startTimes.reduce((min, time) => {
                return time < min ? time : min;
            });
            
            return minTime.substring(0, 5);
        },
        intervalCount() {
            if (!this.user?.availabilities || this.user.availabilities.length === 0) {
                return 13;
            }
            
            // Filter out availabilities with null times (not_attending days)
            const attendingAvailabilities = this.user.availabilities.filter(a => a.start_time !== null && a.end_time !== null);
            
            if (attendingAvailabilities.length === 0) {
                return 13;
            }
            
            const startTimes = attendingAvailabilities.map(a => a.start_time);
            const endTimes = attendingAvailabilities.map(a => a.end_time);
            
            const minTime = startTimes.reduce((min, time) => time < min ? time : min);
            const maxTime = endTimes.reduce((max, time) => time > max ? time : max);
            
            const minHour = parseInt(minTime.split(':')[0]);
            const maxHour = parseInt(maxTime.split(':')[0]);
            const maxMinute = parseInt(maxTime.split(':')[1]);
            
            let intervals = maxHour - minHour;
            if (maxMinute > 0) {
                intervals += 1;
            }
            
            return Math.max(intervals, 1);
        },
        cal () {
            return this.ready ? this.$refs.calendar : null
        },
        nowY () {
            return this.cal ? this.cal.timeToY(this.cal.times.now) + 'px' : '-10px'
        },
    },
    mounted() {
        this.scrollToTime();
        this.updateTime();

        setTimeout(() => {
            this.getCalendarEvents({ start: this.$refs.calendar.lastStart, end: this.$refs.calendar.lastEnd });
        }, 1000);
    },
    methods: {
        ...mapActions("schedule", ["ActionDoGetCalendarEvents"]),
        ...mapActions("sessions", ["ActionDoUpdateSession"]),
        getCalendarEvents({ start, end }) {
            this.ActionDoGetCalendarEvents({
                start_date: start.date,
                end_date: end.date
            }).then(() => {
                this.ready = true;
            })
        },
        viewDay({ date }) {
            this.calendar_value = date;
            this.calendar_type = 'day';
        },
        showEvent({ nativeEvent, event }) {
            nativeEvent.stopPropagation();
            if (event?.session) {
                this.$emit("clickEvent", event);
                this.selectedEvent = event;
            } else {
                this.$emit("clickEvent", null);
                this.selectedEvent = null;
            }
        },
        getCurrentTime () {
            return this.cal ? this.cal.times.now.hour * 60 + this.cal.times.now.minute : 0;
        },
        scrollToTime () {
            const time = this.getCurrentTime();
            const first = Math.max(0, time - (time % 30) - 30);

            this.cal?.scrollToTime(first);
        },
        updateTime () {
            setInterval(() => this.cal.updateTimes(), 60 * 1000);
        },
        getEventColor(event) {
            let color = '';

            switch (event.type) {
                case 'birthday':
                    color = 'event-birthday';
                    break;
                case 'predicted':
                    color = 'event-predicted';
                    break;
                case 'scheduled':
                    color = 'event-scheduled';
                    break;
                case 'payment-pending':
                    color = 'event-payment-pending';
                    break;
                case 'attended':
                    color = 'event-attended';
                    break;
                case 'rescheduled':
                    color = 'event-rescheduled';
                    break;
                case 'missed':
                    color = 'event-missed';
                    break;
                default:
                    color = 'event-extra';
                    break;
            }

            return color;
        },
        checkTimeDiscrepancy(event) {
            if (!event.session || !event.session.google_calendar_event) {
                return false;
            }

            const googleEvent = event.session.google_calendar_event.event_data;
            const googleStart = dayjs(googleEvent.start);
            const googleEnd = dayjs(googleEvent.end);
            
            const systemStart = dayjs(event.start);
            const systemEnd = dayjs(event.end);

            // Check if start or end times differ by more than a minute
            const startDiff = Math.abs(googleStart.diff(systemStart, 'minute'));
            const endDiff = Math.abs(googleEnd.diff(systemEnd, 'minute'));

            return startDiff > 1 || endDiff > 1;
        },
        startDrag({ event, timed }) {
            // Only allow dragging events that have a session
            if (event && event.session && timed) {
                this.dragEvent = event;
                this.dragTime = null;
                this.hasDragged = false;
                this.isDraggingReady = false;
                
                if (this.dragTimeout) clearTimeout(this.dragTimeout);
                
                this.dragTimeout = setTimeout(() => {
                    this.isDraggingReady = true;
                }, 400); // 400ms hold delay to enable dragging
            }
        },
        mouseMove(tms) {
            const mouse = this.toTime(tms);

            if (this.dragEvent && mouse && this.isDraggingReady) {
                this.hasDragged = true;
                const start = this.dragTime !== null ? this.dragTime : mouse;
                const end = mouse;
                const duration = dayjs(this.dragEvent.end).diff(dayjs(this.dragEvent.start), 'minute');
                
                // Round to 15 minute intervals
                const roundedTime = this.roundTime(end);
                const newStart = dayjs(roundedTime);
                const newEnd = newStart.add(duration, 'minute');

                this.updateEventTime(this.dragEvent, newStart.format('YYYY-MM-DD HH:mm'), newEnd.format('YYYY-MM-DD HH:mm'));
                this.dragTime = start;
            }
        },
        endDrag() {
            if (this.dragTimeout) clearTimeout(this.dragTimeout);

            if (this.dragEvent && this.dragEvent.session && this.hasDragged) {
                const confirmDrag = window.confirm("Deseja realmente remarcar esta sessão para o novo horário?");
                
                if (!confirmDrag) {
                    // Refresh calendar to revert visual changes
                    this.getCalendarEvents({ 
                        start: this.$refs.calendar.lastStart, 
                        end: this.$refs.calendar.lastEnd 
                    });
                    this.cancelDrag();
                    return;
                }

                const sessionId = this.dragEvent.session.id;
                const newDateTime = this.dragEvent.start;
                
                // Update session via API
                this.ActionDoUpdateSession({
                    session_id: sessionId,
                    customer_id: this.dragEvent.session.customer_id,
                    service_id: this.dragEvent.session.service_id,
                    session_date: newDateTime,
                    attended: this.dragEvent.session.attended,
                    comments: this.dragEvent.session.comments,
                    private_note: this.dragEvent.session.private_note,
                    session_value: this.dragEvent.session.session_value,
                    session_duration: this.dragEvent.session.session_duration,
                    session_record: this.dragEvent.session.session_record,
                    finished: this.dragEvent.session.finished,
                    mounted: this.dragEvent.session.mounted,
                    rescheduled: this.dragEvent.session.rescheduled
                }).then(() => {
                    // Refresh calendar events
                    this.getCalendarEvents({ 
                        start: this.$refs.calendar.lastStart, 
                        end: this.$refs.calendar.lastEnd 
                    });
                }).catch(error => {
                    console.error('Error updating session:', error);
                    // Refresh calendar to revert visual changes
                    this.getCalendarEvents({ 
                        start: this.$refs.calendar.lastStart, 
                        end: this.$refs.calendar.lastEnd 
                    });
                });
            }
            
            this.cancelDrag();
        },
        cancelDrag() {
            if (this.dragTimeout) clearTimeout(this.dragTimeout);
            this.dragEvent = null;
            this.dragTime = null;
            this.createEvent = null;
            this.createStart = null;
            this.extendOriginal = null;
            this.isDraggingReady = false;
            this.hasDragged = false;
        },
        roundTime(time, down = true) {
            const roundTo = 15; // minutes
            const roundDownTime = roundTo * 60 * 1000;

            return down
                ? time - (time % roundDownTime)
                : time + (roundDownTime - (time % roundDownTime));
        },
        toTime(tms) {
            return new Date(
                tms.year,
                tms.month - 1,
                tms.day,
                tms.hour,
                tms.minute
            ).getTime();
        },
        updateEventTime(event, start, end) {
            event.start = start;
            event.end = end;
        }
    }
}
</script>

<style lang="scss">
.v-current-time {
    height: 2px;
    background-color: #ea4335;
    position: absolute;
    left: -1px;
    right: 0;
    pointer-events: none;

    &.first::before {
        content: '';
        position: absolute;
        background-color: #ea4335;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        margin-top: -5px;
        margin-left: -6.5px;
    }
}

.selected-event {
    outline: 3px solid #000000 !important;
    outline-offset: -3px;
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.8) !important;
    position: relative;
    z-index: 10;
}

.selected-event-future {
    outline: 3px solid #000000 !important;
    outline-offset: -3px;
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.8) !important;
    position: relative;
    z-index: 10;
}

.dragging-event {
    opacity: 0.7;
    cursor: grabbing !important;
}
</style>
